import {
  fetchWeatherSnapshot,
  formatTempDisplay,
  formatWindDisplay,
} from "./weatherApi";
import { applyWeatherWidgetStyle } from "./weatherWidgetStyle";
import { weatherIconEmoji } from "./weatherEmoji";

function subsOf(group) {
  return group._objects || group.getObjects?.() || [];
}

function setText(subs, name, text) {
  const t = subs.find((c) => c.name === name);
  if (!t || t.text === text) return false;
  t.set({ text });
  if (typeof t.initDimensions === "function") t.initDimensions();
  return true;
}

function applySnapshotToGroup(group, snap) {
  const variant = group.weatherObj?.variant || "glass-card";
  const unit = snap.unit || group.weatherObj?.unit || "metric";
  const subs = subsOf(group);
  let changed = false;
  const tempStr = formatTempDisplay(snap.temp, unit);
  const desc =
    snap.description.charAt(0).toUpperCase() + snap.description.slice(1);
  const meta = `Humidity ${snap.humidity}% · Wind ${formatWindDisplay(snap.wind, unit)}`;
  const emoji = weatherIconEmoji(snap.iconCode, snap.description);

  changed = setText(subs, "weather-icon-text", emoji) || changed;

  if (variant === "minimal") {
    const line = `${tempStr} · ${desc} · ${snap.cityLabel}`;
    changed = setText(subs, "weather-minimal-text", line) || changed;
  } else if (variant === "compact") {
    changed = setText(subs, "weather-temp-text", tempStr) || changed;
    changed = setText(subs, "weather-city-text", snap.cityLabel) || changed;
    changed =
      setText(subs, "weather-desc-text", desc) || changed;
  } else {
    changed = setText(subs, "weather-city-text", snap.cityLabel) || changed;
    changed = setText(subs, "weather-temp-text", tempStr) || changed;
    changed = setText(subs, "weather-desc-text", desc) || changed;
    changed = setText(subs, "weather-meta-text", meta) || changed;
  }

  group.weatherObj = {
    ...(group.weatherObj || {}),
    city: snap.cityLabel,
    lat: snap.lat,
    lng: snap.lng,
    unit,
    cached: { ...snap },
  };
  applyWeatherWidgetStyle(group, group.weatherObj);
  if (typeof group.setCoords === "function") group.setCoords();
  return changed;
}

/**
 * @param {import('fabric').fabric.Canvas | null | undefined} canvas
 * @returns {Promise<boolean>}
 */
export async function refreshWeatherWidgets(canvas) {
  if (!canvas || typeof canvas.getObjects !== "function") return false;

  const widgets = canvas
    .getObjects()
    .filter((o) => o.type === "group" && o.eleType === "weather-widget");

  if (!widgets.length) return false;

  let changed = false;
  await Promise.all(
    widgets.map(async (group) => {
      const wo = group.weatherObj || {};
      try {
        const snap = await fetchWeatherSnapshot({
          lat: wo.lat,
          lng: wo.lng,
          city: wo.city,
          unit: wo.unit,
        });
        applySnapshotToGroup(group, snap);
        changed = true;
      } catch (_) {
        /* keep last cached / placeholder text */
      }
    })
  );

  if (changed) canvas.requestRenderAll();
  return changed;
}
